import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AdminChrome from "../components/AdminChrome";
import { useRequireAdmin } from "./AdminLogin";
import { LinkIcon, NETWORKS } from "../components/linkIcons";

export default function AdminBranding() {
  useRequireAdmin();
  const fileRef = useRef(null);
  const [b, setB] = useState({ background_image_url: "", use_background_image: false, links: [], site_flags: { live_status_enabled: false, ads_enabled: false }, atmosphere_mode: "off" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/branding")
      .then((r) => {
        const next = r.data || {};
        setB({
          background_image_url: next.background_image_url || "",
          use_background_image: Boolean(next.use_background_image && next.background_image_url),
          logo_url: next.logo_url || "",
          site_name: next.site_name || "",
          links: (next.links || []).sort((a, c) => (a.order || 0) - (c.order || 0)),
          site_flags: {
            live_status_enabled: Boolean(next.site_flags?.live_status_enabled),
            ads_enabled: Boolean(next.site_flags?.ads_enabled),
          },
          atmosphere_mode: ["off", "lightning", "starfield", "rain"].includes(next.atmosphere_mode) ? next.atmosphere_mode : "off",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  // Background-image upload is parked (see the greyed-out "Unavailable" block
  // below); no upload handler is wired to the dropzone/file input.

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await api.put("/branding", b);
      setMsg("saved");
    } catch (e) { setMsg(e.response?.data?.detail || "save failed"); }
    finally { setSaving(false); }
  };

  const updateLink = (id, changes) => setB((current) => ({ ...current, links: current.links.map((link) => link.id === id ? { ...link, ...changes } : link) }));
  const addLink = () => setB((current) => ({ ...current, links: [...current.links, { id: `new-${Date.now()}`, label: "", url: "", network: "website", enabled: true, order: current.links.length * 10 }] }));
  const removeLink = (id) => setB((current) => ({ ...current, links: current.links.filter((link) => link.id !== id) }));
  const moveLink = (index, direction) => setB((current) => {
    const links = [...current.links]; const target = index + direction;
    if (target < 0 || target >= links.length) return current;
    [links[index], links[target]] = [links[target], links[index]];
    return { ...current, links: links.map((link, order) => ({ ...link, order: order * 10 })) };
  });

  if (loading) return <div className="xs-page" style={{ padding: 80, textAlign: "center" }}>loading…</div>;

  return (
    <div className="App">
      <Header />
      <main className="xs-page" data-testid="admin-branding-page">
        <div className="xs-container" style={{ maxWidth: 760 }}>
          <AdminChrome />
          <h1 style={{ margin: "0 0 4px", color: "#ffde16" }}>branding</h1>

          <p style={{ opacity: 0.85, marginTop: 16, fontSize: "0.95em" }}>
            background image upload is temporarily unavailable and greyed out below. the public site stays a pure black field.
            pick an atmosphere mode instead — only one effect runs at a time.
          </p>

          <label className="xs-label">background image — unavailable</label>
          <div
            className="xs-dropzone"
            aria-disabled="true"
            style={{ opacity: 0.45, pointerEvents: "none", filter: "grayscale(1)" }}
            data-testid="branding-dropzone"
          >
            {b.background_image_url ? (
              <div>
                <div style={{ fontSize: "0.85em", wordBreak: "break-all", marginBottom: 6 }}>{b.background_image_url}</div>
                <div style={{ fontSize: "0.75em", opacity: 0.7 }}>unavailable — background image changes are disabled</div>
              </div>
            ) : (
              <div>
                <div>background image — unavailable</div>
                <div style={{ fontSize: "0.75em", opacity: 0.7, marginTop: 4 }}>upload is disabled while this feature is parked</div>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: "none" }}
                   disabled data-testid="branding-file-input" />
          </div>

          <label className="xs-label" style={{ display: "flex", alignItems: "center", gap: 10, opacity: 0.45, filter: "grayscale(1)" }}>
            <input
              type="checkbox"
              checked={Boolean(b.background_image_url) && b.use_background_image}
              disabled
              data-testid="branding-toggle"
            />
            use uploaded background image — unavailable (site stays pure black)
          </label>

          <div className="xs-site-flags">
            <label className="xs-label"><input type="checkbox" checked={Boolean(b.site_flags?.live_status_enabled)} onChange={(e) => setB({ ...b, site_flags: { ...b.site_flags, live_status_enabled: e.target.checked } })} /> Show online / live checker</label>
            <label className="xs-label"><input type="checkbox" checked={Boolean(b.site_flags?.ads_enabled)} onChange={(e) => setB({ ...b, site_flags: { ...b.site_flags, ads_enabled: e.target.checked } })} /> Show advertisements</label>
          </div>

          <label className="xs-label" htmlFor="branding-atmosphere-mode" style={{ display: "block", marginTop: 14 }}>
            atmosphere overlay (public site backdrop effect — only one mode runs at a time)
          </label>
          <select
            id="branding-atmosphere-mode"
            className="xs-input"
            value={b.atmosphere_mode || "off"}
            onChange={(e) => setB({ ...b, atmosphere_mode: e.target.value })}
            data-testid="branding-atmosphere-mode"
            style={{ maxWidth: 320 }}
          >
            <option value="off">Atmosphere: Off</option>
            <option value="lightning">Atmosphere: Lightning</option>
            <option value="starfield">Atmosphere: Starfield</option>
            <option value="rain">Atmosphere: Rain</option>
          </select>

          <section className="xs-branding-links" data-testid="branding-links-editor">
            <div className="xs-admin-heading"><h2>the links</h2><button className="xs-btn ghost" type="button" onClick={addLink}>+ add link</button></div>
            <div className="xs-branding-link-list">
              {b.links.map((link, index) => (
                <div className={`xs-branding-link-row ${link.enabled ? "" : "disabled"}`} key={link.id}>
                  <LinkIcon network={link.network} size={28} />
                  <select className="xs-input" value={link.network} onChange={(e) => updateLink(link.id, { network: e.target.value })} aria-label="Network">
                    {NETWORKS.map((network) => <option key={network} value={network}>{network}</option>)}
                  </select>
                  <input className="xs-input" value={link.label} onChange={(e) => updateLink(link.id, { label: e.target.value })} placeholder="label" aria-label="Label" />
                  <input className="xs-input" value={link.url} onChange={(e) => updateLink(link.id, { url: e.target.value })} placeholder="URL" aria-label="URL" />
                  <button className="xs-btn ghost" type="button" onClick={() => moveLink(index, -1)} aria-label="Move link up">↑</button>
                  <button className="xs-btn ghost" type="button" onClick={() => moveLink(index, 1)} aria-label="Move link down">↓</button>
                  <button className="xs-btn ghost" type="button" onClick={() => updateLink(link.id, { enabled: !link.enabled })}>{link.enabled ? "hide" : "show"}</button>
                  <button className="xs-btn danger" type="button" onClick={() => removeLink(link.id)}>delete</button>
                </div>
              ))}
            </div>
          </section>

          <div style={{ marginTop: 24, display: "flex", gap: 10 }}>
            <button className="xs-btn primary" onClick={save} disabled={saving} data-testid="branding-save">
              {saving ? "saving…" : "save"}
            </button>
            {msg && <div style={{ alignSelf: "center", fontSize: "0.85em", opacity: 0.85 }}>{msg}</div>}
          </div>
        </div>
      </main>
      <Footer show />
    </div>
  );
}
